import { useCallback, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { log } from '@/lib/prodLogger';
import type { Database } from '@/integrations/supabase/types';
import type { PlatformRole } from '@/lib/rbac/roles';

type UserRole = Database['public']['Enums']['app_role'];

interface RoleData {
  roles: UserRole[];
  isAdmin: boolean;
  isReseller: boolean;
  isVerifier: boolean;
  isOperator: boolean;
  isConsultant: boolean;
}

interface UseUserRoleReturn {
  roles: UserRole[];
  loading: boolean;
  error: string | null;
  hasRole: (role: UserRole) => boolean;
  isAdmin: boolean;
  isReseller: boolean;
  isVerifier: boolean;
  isOperator: boolean;
  isConsultant: boolean;
  platformRole: PlatformRole | null;
  canAccessAccounts: boolean;
  canAccessVerification: boolean;
  canAccessOperations: boolean;
  refetch: () => Promise<void>;
}

/**
 * Fetches role data from server with parallel RPC calls
 * Server-side functions are authoritative for security
 */
async function fetchRoleData(userId: string): Promise<RoleData> {
  const [rolesResult, adminResult, resellerResult, verifierResult] = await Promise.all([
    supabase.from('user_roles').select('role').eq('user_id', userId),
    supabase.rpc('check_is_admin'),
    supabase.rpc('is_reseller'),
    supabase.rpc('is_verifier'),
  ]);

  const roles = rolesResult.data?.map(r => r.role) ?? [];

  // Server-side RPC checks are authoritative, fallback to roles table
  return {
    roles,
    isAdmin: adminResult.data === true || roles.includes('admin'),
    isReseller: resellerResult.data === true || roles.includes('reseller'),
    isVerifier: verifierResult.data === true || roles.includes('verifier'),
    isOperator: roles.includes('operator'),
    isConsultant: roles.includes('consultant'),
  };
}

/**
 * useUserRole - Cached role fetching with React Query
 * 
 * Features:
 * - Server-side validation via RPC functions
 * - 5-minute cache to reduce database load
 * - Automatic background refetch
 * - Manual refetch available via refetch()
 */
export function useUserRole(): UseUserRoleReturn {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch: queryRefetch } = useQuery({
    queryKey: ['userRole', user?.id],
    queryFn: () => fetchRoleData(user!.id),
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes - cache roles
    gcTime: 10 * 60 * 1000,   // 10 minutes - garbage collection
  });

  // ═══════════════════════════════════════════════════════════════
  // REALTIME SUBSCRIPTION - Invalidate cache on role changes
  // ═══════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel(`user-roles-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*', // INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'user_roles',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          log.debug('[Realtime] user_roles change', { eventType: payload.eventType });
          // Invalidate cache - React Query will refetch automatically
          queryClient.invalidateQueries({ queryKey: ['userRole', user.id] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, queryClient]);

  const roles = data?.roles ?? [];
  const isAdmin = data?.isAdmin ?? false;
  const isReseller = data?.isReseller ?? false;
  const isVerifier = data?.isVerifier ?? false;
  const isOperator = data?.isOperator ?? false;
  const isConsultant = data?.isConsultant ?? false;

  // Computed platform role (highest privilege first)
  const platformRole: PlatformRole | null = useMemo(() => {
    if (isAdmin) return 'admin';
    if (isVerifier) return 'verifier';
    if (isOperator) return 'operator';
    if (isConsultant) return 'consultant';
    if (isReseller) return 'reseller';
    if (user) return 'user';
    return null;
  }, [isAdmin, isVerifier, isOperator, isConsultant, isReseller, user]);

  // Convenience computed properties for route access
  const canAccessAccounts = isAdmin || isReseller;
  const canAccessVerification = isAdmin || isVerifier;
  const canAccessOperations = isAdmin || isOperator;

  const hasRole = useCallback(
    (role: UserRole) => roles.includes(role),
    [roles]
  );

  const refetch = useCallback(async () => {
    await queryRefetch();
  }, [queryRefetch]);

  return {
    roles,
    loading: isLoading,
    error: error?.message ?? null,
    hasRole,
    isAdmin,
    isReseller,
    isVerifier,
    isOperator,
    isConsultant,
    platformRole,
    canAccessAccounts,
    canAccessVerification,
    canAccessOperations,
    refetch,
  };
}
